import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Database, Eye, Lock, Trash2, Mail, Cookie, Settings, FileText } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Privacy Policy & Data Protection | Flash-Read',
    description: 'Flash-Read Privacy Policy. Learn how we collect, use, and protect your personal data. Your reading content stays private — processed locally in your browser.',
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Home
          </button>
           <h1 className="text-4xl font-bold mb-4 text-center">
             <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
               Privacy Policy
             </span>
           </h1>
           <p className="text-lg text-slate-600 dark:text-slate-400 text-center">Last updated: April 6, 2026</p>
        </div>

        {/* Introduction */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 mb-8 border border-slate-100 dark:border-slate-800">
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
            Flash-Read ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application. Please read this policy carefully to understand our practices regarding your personal data.
          </p>
        </div>

        {/* Table of Contents */}
        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-6 mb-8 border border-slate-100 dark:border-slate-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Table of Contents</h2>
          <ol className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
            <li><a href="#information" className="hover:text-primary transition-colors">1. Information We Collect</a></li>
            <li><a href="#cookies" className="hover:text-primary transition-colors">2. Cookies and Tracking Technologies</a></li>
            <li><a href="#how-we-use" className="hover:text-primary transition-colors">3. How We Use Your Information</a></li>
            <li><a href="#third-party" className="hover:text-primary transition-colors">4. Third-Party Integrations</a></li>
            <li><a href="#sharing" className="hover:text-primary transition-colors">5. Sharing and Disclosure</a></li>
            <li><a href="#storage" className="hover:text-primary transition-colors">6. Data Storage and Security</a></li>
            <li><a href="#retention" className="hover:text-primary transition-colors">7. Data Retention</a></li>
            <li><a href="#rights" className="hover:text-primary transition-colors">8. Your Rights and Choices</a></li>
            <li><a href="#cipa" className="hover:text-primary transition-colors">9. California Invasion of Privacy Act (CIPA) Compliance</a></li>
            <li><a href="#children" className="hover:text-primary transition-colors">10. Children's Privacy</a></li>
            <li><a href="#changes" className="hover:text-primary transition-colors">11. Changes to This Policy</a></li>
            <li><a href="#contact" className="hover:text-primary transition-colors">12. Contact Us</a></li>
          </ol>
        </div>

        {/* Section 1 */}
        <section id="information" className="mb-10">
          <div className="flex items-center mb-4">
            <Database className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">1. Information We Collect</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">1.1 Information You Provide</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Account Information:</strong> When you create an account, we collect your email address and any profile information you choose to provide (display name, bio, reading preferences).</li>
              <li><strong>Payment Information:</strong> If you subscribe to a paid plan, payment details are processed by our payment processor (Stripe). We do not store your credit card information on our servers.</li>
              <li><strong>Uploaded Content:</strong> If you choose to upload your own text files (.txt, .epub) for use with the RSVP reader, we process this content locally in your browser.</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">1.2 Information Collected Automatically</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Reading Statistics:</strong> We collect data about your reading activity, including words read, reading speed (WPM), session duration, and reading history.</li>
              <li><strong>Progress Data:</strong> Your reading position, completed chapters, and book progress are stored to allow you to resume where you left off.</li>
              <li><strong>Achievements and XP:</strong> We track your earned XP, unlocked achievements, reading streaks, and level progression.</li>
              <li><strong>Device and Browser Information:</strong> We may collect basic technical information such as browser type, device type, and operating system for service optimization.</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">1.3 Information Stored Locally</h3>
            <p>
              The following data is stored locally in your browser using <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-sm">localStorage</code>:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Reading progress and session statistics</li>
              <li>Theme preferences (light/dark mode)</li>
              <li>Guest session counts (for the 3-session free trial)</li>
              <li>Onboarding tour completion status</li>
              <li>User settings and preferences</li>
            </ul>
          </div>
        </section>

        {/* Section 2: Cookies and Tracking */}
        <section id="cookies" className="mb-10">
          <div className="flex items-center mb-4">
            <Cookie className="h-6 w-6 text-amber-600 dark:text-amber-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">2. Cookies and Tracking Technologies</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              We use cookies and similar tracking technologies to enhance your experience and analyze site usage. 
              Our consent management system ensures compliance with privacy laws including the California Invasion 
              of Privacy Act (CIPA).
            </p>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">2.1 Cookie Categories</h3>
            <p>We classify cookies into the following categories:</p>
            
            <div className="space-y-4 ml-4">
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-slate-900 dark:text-white">Necessary Cookies (Always Active)</h4>
                <p>Essential for the website to function properly. These cannot be disabled as they are required for core functionality such as:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>User authentication and session management</li>
                  <li>Security features and fraud prevention</li>
                  <li>Load balancing and server routing</li>
                  <li>Consent preference storage</li>
                </ul>
              </div>

              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-slate-900 dark:text-white">Analytics Cookies (Opt-In)</h4>
                <p>Help us understand how visitors interact with our website by collecting anonymous data:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Pages visited and time spent on each page</li>
                  <li>Reading speed and session duration</li>
                  <li>Feature usage patterns</li>
                  <li>Error reporting and performance monitoring</li>
                </ul>
              </div>

              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="font-semibold text-slate-900 dark:text-white">Marketing Cookies (Opt-In)</h4>
                <p>Used to deliver personalized advertisements and measure their effectiveness:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Ad campaign performance tracking</li>
                  <li>Retargeting and remarketing</li>
                  <li>Social media integration</li>
                  <li>Cross-site tracking for advertising</li>
                </ul>
              </div>

              <div className="border-l-4 border-pink-500 pl-4">
                <h4 className="font-semibold text-slate-900 dark:text-white">Personalization Cookies (Opt-In)</h4>
                <p>Allow us to remember your preferences and provide a customized experience:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Theme and display preferences</li>
                  <li>Reading speed settings</li>
                  <li>Content recommendations</li>
                  <li>Language and localization settings</li>
                </ul>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">2.2 Consent Management</h3>
            <p>
              We use a Consent Management Platform (CMP) that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Blocks all non-essential scripts</strong> until you provide explicit consent</li>
              <li><strong>Honors your "Decline" choice</strong> by preventing tracking scripts from loading</li>
              <li><strong>Stores your preferences</strong> in your browser's local storage</li>
              <li><strong>Allows you to change your preferences</strong> at any time</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">2.3 Third-Party Tracking Tools</h3>
            <p>We may use the following third-party services (only with your consent):</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Google Analytics:</strong> For website traffic analysis and user behavior tracking</li>
              <li><strong>Meta Pixel:</strong> For advertising campaign measurement (if applicable)</li>
              <li><strong>Session Replay Tools:</strong> For understanding user interactions (if applicable)</li>
            </ul>
            <p className="mt-2">
              <strong>Important:</strong> These tools are <strong>NOT loaded</strong> until you explicitly consent 
              to the relevant cookie category. If you decline, these tools will not be loaded and no data 
              will be transmitted to these third parties.
            </p>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">2.4 Managing Your Cookie Preferences</h3>
            <p>You can manage your cookie preferences at any time by:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Clicking the cookie settings link in the footer of our website</li>
              <li>Using your browser's built-in cookie management tools</li>
              <li>Clearing your browser's local storage</li>
            </ul>
            <p className="mt-2">
              <strong>Note:</strong> Disabling certain cookies may affect the functionality of our service.
            </p>
          </div>
        </section>

        {/* Section 3: How We Use Your Information */}
        <section id="how-we-use" className="mb-10">
          <div className="flex items-center mb-4">
            <Eye className="h-6 w-6 text-purple-600 dark:text-purple-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">3. How We Use Your Information</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Service Delivery:</strong> To provide, maintain, and improve the Flash-Read application, including saving and restoring your reading progress.</li>
              <li><strong>Personalization:</strong> To customize your experience, such as remembering your preferred reading speed, theme, and font settings.</li>
              <li><strong>Gamification:</strong> To calculate and display XP, achievements, streaks, and reading statistics.</li>
              <li><strong>Billing:</strong> To process subscription payments and manage your paid plan through Stripe.</li>
              <li><strong>Communication:</strong> To send you important account notifications, service updates, and (with your consent) marketing communications.</li>
              <li><strong>Analytics:</strong> To understand how users interact with the service and identify areas for improvement.</li>
            </ul>
          </div>
        </section>

        {/* Section 4: Third-Party Integrations */}
        <section id="third-party" className="mb-10">
          <div className="flex items-center mb-4">
            <Database className="h-6 w-6 text-orange-600 dark:text-orange-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">4. Third-Party Integrations</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              Flash-Read relies on the following third-party service providers to deliver our service. These providers process data on our behalf and are contractually obligated to protect your information.
            </p>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">4.1 Supabase</h3>
            <p><strong>Purpose:</strong> Database storage, user authentication, and cloud functions</p>
            <p><strong>Data Processed:</strong></p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Email address and encrypted password (for authentication)</li>
              <li>Reading progress, XP, level, and streak data</li>
              <li>Account settings and preferences</li>
              <li>Uploaded content metadata (not the content itself, which stays local)</li>
            </ul>
            <p className="mt-2"><strong>Security:</strong> PostgreSQL database with row-level security, encryption at rest and in transit, SOC 2 Type II certified</p>
            <p><strong>Privacy Policy:</strong> <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">https://supabase.com/privacy</a></p>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">4.2 Stripe</h3>
            <p><strong>Purpose:</strong> Payment processing and subscription management</p>
            <p><strong>Data Processed:</strong></p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Payment card information (processed directly by Stripe, not stored on our servers)</li>
              <li>Billing address and contact information</li>
              <li>Subscription status and payment history</li>
            </ul>
            <p className="mt-2"><strong>Security:</strong> PCI DSS Level 1 certified (the highest level of payment security certification)</p>
            <p><strong>Privacy Policy:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">https://stripe.com/privacy</a></p>

            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Note:</strong> Both Supabase and Stripe are considered "necessary" service providers under our consent management system. Their services are required for core functionality (account creation, authentication, and payments). You can request deletion of your data from these providers by contacting us.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5: Sharing and Disclosure */}
        <section id="sharing" className="mb-10">
          <div className="flex items-center mb-4">
            <Database className="h-6 w-6 text-emerald-600 dark:text-emerald-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">5. Sharing and Disclosure</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
             <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
             <ul className="list-disc list-inside space-y-2 ml-4">
               <li><strong>Service Providers:</strong> We use third-party services to operate Flash-Read, including:
                 <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                   <li><strong>Supabase (supabase.com):</strong> For database storage, user authentication, and cloud functions. Supabase stores your email address, encrypted password, reading progress, XP/level data, and account settings. Supabase uses PostgreSQL with row-level security and encryption at rest and in transit. See <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Supabase Privacy Policy</a>.</li>
                   <li><strong>Stripe (stripe.com):</strong> For payment processing and subscription management. Stripe processes your payment card information, billing address, and subscription status. We do not store your full credit card details on our servers. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Stripe Privacy Policy</a>.</li>
                 </ul>
               </li>
              <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, regulation, legal process, or governmental request.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
              <li><strong>Protection of Rights:</strong> We may disclose information to protect the rights, property, or safety of Flash-Read, our users, or others.</li>
            </ul>
          </div>
        </section>

        {/* Section 6: Data Storage and Security */}
        <section id="storage" className="mb-10">
          <div className="flex items-center mb-4">
            <Lock className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">6. Data Storage and Security</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              <strong>Storage:</strong> Your account data and reading statistics are stored in Supabase, a PostgreSQL-based cloud database. Supabase employs industry-standard security measures including encryption at rest and in transit.
            </p>
            <p>
              <strong>Security Measures:</strong> We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Encrypted connections (HTTPS) for all data transmission</li>
              <li>Secure authentication via Supabase Auth</li>
              <li>Row-level security policies in our database</li>
              <li>Regular security audits and updates</li>
            </ul>
            <p>
              <strong>No Guarantee:</strong> While we strive to protect your personal data, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </div>
        </section>

        {/* Section 7: Data Retention */}
        <section id="retention" className="mb-10">
          <div className="flex items-center mb-4">
            <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">7. Data Retention</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              We retain your personal data for as long as your account is active or as needed to provide you with the service. If you delete your account, we will delete or anonymize your personal data within 30 days, unless we are required to retain it for legal, tax, or legitimate business purposes.
            </p>
            <p>
              Local data stored in your browser (localStorage) is removed when you clear your browser data or use your browser's site data management features.
            </p>
          </div>
        </section>

        {/* Section 8: Your Rights and Choices */}
        <section id="rights" className="mb-10">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-emerald-600 dark:text-emerald-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">8. Your Rights and Choices</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>Depending on your location, you may have the following rights regarding your personal data:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete personal data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal obligations.</li>
              <li><strong>Portability:</strong> Request a copy of your data in a machine-readable format.</li>
              <li><strong>Objection:</strong> Object to the processing of your personal data for certain purposes.</li>
              <li><strong>Withdrawal of Consent:</strong> Where processing is based on consent, withdraw consent at any time.</li>
            </ul>
            <p className="mt-4">
              To exercise any of these rights, please contact us through the application or via our support channels. We will respond to your request within 30 days.
            </p>
            <p>
              <strong>California Residents (CCPA):</strong> You have the right to know what personal information we collect, the right to delete it, and the right to opt out of its sale. We do not sell personal information.
            </p>
            <p>
              <strong>European Economic Area (GDPR):</strong> If you are located in the EEA, you have additional rights under the General Data Protection Regulation, including the right to lodge a complaint with a supervisory authority.
            </p>
          </div>
        </section>

        {/* Section 9: CIPA Compliance */}
        <section id="cipa" className="mb-10">
          <div className="flex items-center mb-4">
            <FileText className="h-6 w-6 text-red-600 dark:text-red-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">9. California Invasion of Privacy Act (CIPA) Compliance</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              The California Invasion of Privacy Act (CIPA) (California Penal Code §§ 630-638) is a state law 
              that protects the privacy of communications. We take CIPA compliance seriously and have implemented 
              the following measures:
            </p>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">9.1 Our CIPA Compliance Measures</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>No Pre-Consent Tracking:</strong> We do not load any tracking scripts (including Google Analytics, Meta Pixel, or similar tools) until you explicitly consent to their use.</li>
              <li><strong>Hard Block on Decline:</strong> If you click "Decline" or opt out of non-essential cookies, we implement a hard block that prevents those tracking scripts from loading.</li>
              <li><strong>No Data Transmission Without Consent:</strong> No user data is transmitted to third parties without your explicit consent.</li>
              <li><strong>Script Blocking Technology:</strong> We use a script manager that intercepts and blocks unauthorized script injections.</li>
              <li><strong>Consent Verification:</strong> All data transmissions are verified against your consent preferences before being sent.</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">9.2 What This Means for You</h3>
            <p>
              Under CIPA, unauthorized interception of electronic communications can result in significant penalties. 
              Our compliance measures ensure that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Your browsing activity is not tracked without your knowledge and consent</li>
              <li>Third-party tracking tools cannot access your data unless you explicitly allow it</li>
              <li>You have full control over what data is collected and shared</li>
              <li>Your "Decline" choice is respected and enforced at the technical level</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">9.3 CIPA and "Digital Wiretapping"</h3>
            <p>
              Recent legal interpretations have characterized certain tracking technologies as "digital wiretaps" 
              under CIPA. These include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Session replay tools (e.g., Hotjar, FullStory)</li>
              <li>Marketing pixels (e.g., Meta Pixel, Google Ads)</li>
              <li>Analytics tools that collect detailed user behavior</li>
            </ul>
            <p className="mt-2">
              <strong>Our commitment:</strong> We do not use any of these tools without your explicit consent. 
              If you decline tracking, these tools are completely blocked and cannot collect any data about your session.
            </p>

            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6">9.4 Reporting Concerns</h3>
            <p>
              If you believe that tracking tools are being loaded without your consent, or if your "Decline" 
              choice is not being honored, please contact us immediately. We take all such reports seriously 
              and will investigate promptly.
            </p>
            <p className="mt-2">
              You can verify our compliance by:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Checking your browser's Developer Tools (Network tab) to see what scripts are loaded</li>
              <li>Using browser extensions that block trackers to verify they are not firing</li>
              <li>Contacting us for a copy of our consent audit logs</li>
            </ul>
          </div>
        </section>

        {/* Section 10: Children's Privacy */}
        <section id="children" className="mb-10">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-amber-600 dark:text-amber-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">10. Children's Privacy</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              Flash-Read is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have inadvertently collected personal data from a child under 13, we will take steps to delete such information promptly.
            </p>
            <p>
              If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can take appropriate action.
            </p>
          </div>
        </section>

        {/* Section 11: Changes to This Policy */}
        <section id="changes" className="mb-10">
          <div className="flex items-center mb-4">
            <Eye className="h-6 w-6 text-slate-600 dark:text-slate-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">11. Changes to This Policy</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. The most current version will always be available on this page with an updated revision date.
            </p>
            <p>
              For material changes, we will notify users via email or through a prominent notice within the application at least 30 days before the changes take effect.
            </p>
          </div>
        </section>

        {/* Section 12: Contact Us */}
        <section id="contact" className="mb-10">
          <div className="flex items-center mb-4">
            <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">12. Contact Us</h2>
          </div>
          <div className="space-y-4 text-slate-700 dark:text-slate-300 leading-relaxed">
            <p>
              If you have questions or concerns, please contact us at{' '}
              <a href="mailto:support@flash-read.com" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                support@flash-read.com
              </a>.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
};

export default PrivacyPage;
